import { useTranslation } from "@warpme/i18n"
import { WARP_ME_EXTERNAL_LINKS } from "@warpme/ui"
import type { FC } from "react"
import { Link } from "react-router"

import { Icon } from "@/components/ui"
import { cn } from "@/lib/utils"

interface Props {
  className?: string
}

export const Footer: FC<Props> = ({ className }) => {
  const { t } = useTranslation("consumer")

  return (
    <footer className={cn("flex flex-col gap-5 pt-[52px] bg-bg-white", className)}>
      {/* Navigation Bar */}
      <nav className="px-4 pt-3 pb-2 bg-bg-primary">
        <div className="flex justify-between gap-3 max-w-screen-md mx-auto">
          <Link to="/" className="flex flex-col gap-1 items-center">
            <div className="size-[60px] rounded-full bg-bg-white flex items-center justify-center">
              <Icon name="explore" className="size-7" fillColor="var(--color-primary)" />
            </div>
            <span className="text-label-sm font-semibold text-text text-center leading-[1.35]">
              {t("navigation.searchTickets")}
            </span>
          </Link>
          <Link to="/purchases" className="flex flex-col gap-1 items-center">
            <div className="size-[60px] rounded-full bg-bg-white flex items-center justify-center">
              <Icon
                name="confirmation-number"
                className="size-7"
                fillColor="var(--color-primary)"
              />
            </div>
            <span className="text-label-sm font-semibold text-text text-center leading-[1.35]">
              {t("navigation.purchasedTickets")}
            </span>
          </Link>
          <Link to="/account" className="flex flex-col gap-1 items-center">
            <div className="size-[60px] rounded-full bg-bg-white flex items-center justify-center">
              <Icon name="face" className="size-7" fillColor="var(--color-primary)" />
            </div>
            <span className="text-label-sm font-semibold text-text text-center leading-[1.35]">
              {t("navigation.accountInfo")}
            </span>
          </Link>
        </div>
      </nav>

      {/* Links Section */}
      <div className="flex gap-[13px] px-4 w-full max-w-screen-md mx-auto">
        {/* Support Column */}
        <div className="flex-1 flex flex-col gap-1">
          <div className="border-b border-border pb-2">
            <span className="text-label-lg font-light text-gray leading-[1.35]">
              {t("navigation.support")}
            </span>
          </div>
          <div className="flex flex-col">
            <a
              href={WARP_ME_EXTERNAL_LINKS.support.consumerFaq}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-body-sm font-semibold text-text leading-[1.5]"
            >
              {t("navigation.faq")}
            </a>
            <a
              href={WARP_ME_EXTERNAL_LINKS.support.company}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-body-sm font-semibold text-text leading-[1.5]"
            >
              {t("navigation.company")}
            </a>
            <a
              href={WARP_ME_EXTERNAL_LINKS.support.contact}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-body-sm font-semibold text-text leading-[1.5]"
            >
              {t("navigation.contact")}
            </a>
          </div>
        </div>

        {/* Other Column */}
        <div className="flex-1 flex flex-col gap-1">
          <div className="border-b border-border pb-2">
            <span className="text-label-lg font-light text-gray leading-[1.35]">
              {t("navigation.other")}
            </span>
          </div>
          <div className="flex flex-col">
            <a
              href={WARP_ME_EXTERNAL_LINKS.legal.consumerTerms}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-label-sm font-light text-text leading-[1.35]"
            >
              {t("navigation.terms")}
            </a>
            <a
              href={WARP_ME_EXTERNAL_LINKS.legal.privacyPolicy}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-label-sm font-light text-text leading-[1.35]"
            >
              {t("navigation.privacy")}
            </a>
            <a
              href={WARP_ME_EXTERNAL_LINKS.legal.specifiedCommercialTransactions}
              target="_blank"
              rel="noreferrer"
              className="h-9 flex items-center text-label-sm font-light text-text leading-[1.35]"
            >
              {t("navigation.commercialLaw")}
            </a>
          </div>
        </div>
      </div>

      {/* Brand Section */}
      <div className="flex flex-col gap-3 items-center px-4">
        <div className="flex items-center gap-1">
          <div className="size-6 rounded-full bg-primary" />
          <span className="text-title-md font-semibold text-text leading-[1.35]">WarpMe</span>
        </div>
        <span className="text-label-sm font-light text-gray text-center leading-[1.35]">
          ©︎WarpMe All Rights Reserved.
        </span>
      </div>

      {/* Domain Section */}
      <div className="border-t border-border flex items-center justify-center px-4 pt-3 pb-5">
        <a
          href={WARP_ME_EXTERNAL_LINKS.consumerSite}
          target="_blank"
          rel="noreferrer"
          className="text-label-sm font-light text-text text-center leading-[1.35]"
        >
          {new URL(WARP_ME_EXTERNAL_LINKS.consumerSite).hostname}
        </a>
      </div>
    </footer>
  )
}
